// Owner: design-system (plan §9.1, owner feedback F3). The brand's mark as the browser-tab icon and the
// last brand this browser saw (accent and mark), for screens that have no workspace of their own (the
// status page, the root error page). No data access here, so every public page can use it.
import { useEffect, useLayoutEffect } from 'react';
import { z } from 'zod';
import { Hex } from '@dataroom/core/doc/color';
import { readStored } from '../../lib/stored';
import { applyBrandAccent } from './accent';

const LAST_BRAND = 'dr.brand.last';

let originalIcon: string | null | undefined;

/** The browser tab shows `href` (the brand's mark); `null` puts the product icon back. */
export function applyFavicon(href: string | null): void {
  if (typeof document === 'undefined') return;
  const link = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
  if (!link) return;
  if (originalIcon === undefined) originalIcon = link.getAttribute('href');
  const next = href ?? originalIcon;
  if (next && link.getAttribute('href') !== next) link.setAttribute('href', next);
}

/** The tab icon while a public page that knows its brand is shown. */
export function usePublicFavicon(href: string | null | undefined): void {
  useLayoutEffect(() => {
    if (!href) return;
    applyFavicon(href);
    return () => applyFavicon(null);
  }, [href]);
}

/** The brand as this browser keeps it: the accent goes into a style sheet, so only a hex colour reads back. */
const RememberedBrand = z.object({
  accent: Hex.nullable().catch(null),
  mark: z.string().max(2048).nullable().catch(null),
});
export type RememberedBrand = z.infer<typeof RememberedBrand>;

/** Remember the brand of the workspace the member works in (written by the app shell). */
export function rememberBrand(brand: RememberedBrand): void {
  try {
    if (brand.accent || brand.mark) localStorage.setItem(LAST_BRAND, JSON.stringify(brand));
    else localStorage.removeItem(LAST_BRAND);
  } catch {
    // Storage unavailable: such screens show the product's own look.
  }
}

const recalledBrand = (): RememberedBrand | null => readStored(LAST_BRAND, RememberedBrand);

/**
 * Screens without a workspace in hand (the status page, the root error page) wear the brand of the
 * workspace this browser last worked in: its accent and its mark as the tab icon, as the app does.
 */
export function useRememberedBrand(): void {
  const brand = recalledBrand();
  const accent = brand?.accent ?? null;
  const mark = brand?.mark ?? null;
  useLayoutEffect(() => {
    if (!accent) return;
    applyBrandAccent(accent);
    return () => applyBrandAccent(null);
  }, [accent]);
  useEffect(() => {
    if (!mark) return;
    applyFavicon(mark);
    return () => applyFavicon(null);
  }, [mark]);
}
